import { collection, query, where, getDocs, orderBy, doc, updateDoc, getDoc } from "firebase/firestore";
import { registrarActualizacionDirecta } from "./registrosService";
import { firestore } from "../firebase";

const CACHE_KEY = 'cached_cuadrillas';

/** Retrocompatibilidad: si no tiene campo activa se considera activa (true). */
export const esCuadrillaActiva = (c) => c.activa !== false;

/**
 * Invalida la caché local de cuadrillas
 */
export const invalidateCuadrillasCache = () => {
    try {
        localStorage.removeItem(CACHE_KEY);
        console.log("🧼 Caché de cuadrillas invalidada.");
    } catch (e) {
        console.error("Error al invalidar la caché de cuadrillas:", e);
    }
};

/**
 * Obtener caché local de cuadrillas
 */
const getCachedCuadrillas = () => {
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) return null;
        const parsed = JSON.parse(cached);
        if (!Array.isArray(parsed)) return null;
        
        // Validar estructura de datos
        const isValid = parsed.every(item => item && typeof item === 'object' && item.hasOwnProperty('data') && item.hasOwnProperty('docId'));
        if (!isValid) {
            console.warn("⚠️ Estructura de caché antigua o inválida detectada. Limpiando...");
            localStorage.removeItem(CACHE_KEY);
            return null;
        }
        return parsed;
    } catch (e) {
        console.error("Error al obtener la caché local de cuadrillas:", e);
        return null;
    }
};

/**
 * Guardar en caché local de cuadrillas
 */
const setCachedCuadrillas = (data) => {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (e) {
        console.error("Error al guardar en caché local de cuadrillas:", e);
    }
};

/**
 * @param {Function} onDataChange
 * @param {{ soloActivas?: boolean, forceRefresh?: boolean }} options
 */
export const getCuadrillasOnce = async (onDataChange, options = {}) => {
    const { soloActivas = true, forceRefresh = false } = options;
    
    let hasServedCache = false;
    if (!forceRefresh) {
        try {
            const cached = getCachedCuadrillas();
            if (cached && Array.isArray(cached) && cached.length > 0) {
                let data = cached.map(item => item.data);
                if (soloActivas) {
                    data = data.filter(esCuadrillaActiva);
                }
                if (typeof onDataChange === 'function') {
                    onDataChange(data);
                }
                hasServedCache = true;
                console.log(`📦 Cuadrillas cargadas desde caché local (Once). Cantidad: ${data.length}`);
            }
        } catch (cacheError) {
            console.error("⚠️ Error procesando caché local de cuadrillas (Once):", cacheError);
            invalidateCuadrillasCache();
        }
    }

    try {
        const cuadrillasCollection = collection(firestore, 'cuadrillas');
        const queryCuadrillas = query(cuadrillasCollection, orderBy('id', 'asc'));
        const snapshot = await getDocs(queryCuadrillas);
        const rawCached = snapshot.docs.map(doc => ({ docId: doc.id, data: doc.data() }));
        
        setCachedCuadrillas(rawCached);

        let data = rawCached.map(item => item.data);
        if (soloActivas) {
            data = data.filter(esCuadrillaActiva);
        }
        if (typeof onDataChange === 'function') {
            onDataChange(data);
        }
        console.log(`🔄 Cuadrillas obtenidas de Firestore y actualizadas en caché. Cantidad: ${data.length}`);
    } catch (error) {
        console.error("Error al obtener los datos de cuadrillas de Firestore:", error);
        if (typeof window !== 'undefined' && !hasServedCache) {
            window.alert(`Error al obtener cuadrillas de la base de datos: ${error.message || error.toString()}`);
        }
        if (!hasServedCache) {
            throw error;
        }
    }
};

/**
 * @param {Function} onDataChange
 * @param {{ soloActivas?: boolean, forceRefresh?: boolean }} options
 */
export const getCuadrillasBasic = async (onDataChange, options = {}) => {
    const { soloActivas = true, forceRefresh = false } = options;

    let hasServedCache = false;
    if (!forceRefresh) {
        try {
            const cached = getCachedCuadrillas();
            if (cached && Array.isArray(cached) && cached.length > 0) {
                let data = cached.map(item => ({ id: item.docId, ...item.data }));
                if (soloActivas) {
                    data = data.filter(d => esCuadrillaActiva(d));
                }
                data = data.map(({ id, codigo, numero, usuario, chapaMovil }) => ({ id, codigo, numero, usuario, chapaMovil }));
                if (typeof onDataChange === 'function') {
                    onDataChange(data);
                }
                hasServedCache = true;
                console.log(`📦 Cuadrillas básicas cargadas desde caché local. Cantidad: ${data.length}`);
            }
        } catch (cacheError) {
            console.error("⚠️ Error procesando caché local de cuadrillas (Basic):", cacheError);
            invalidateCuadrillasCache();
        }
    }

    try {
        const cuadrillasCollection = collection(firestore, 'cuadrillas');
        const queryCuadrillas = query(cuadrillasCollection, orderBy('id', 'asc'));
        const snapshot = await getDocs(queryCuadrillas);
        const rawCached = snapshot.docs.map(doc => ({ docId: doc.id, data: doc.data() }));

        setCachedCuadrillas(rawCached);

        let data = rawCached.map(item => ({ id: item.docId, ...item.data }));
        if (soloActivas) {
            data = data.filter(d => esCuadrillaActiva(d));
        }
        data = data.map(({ id, codigo, numero, usuario, chapaMovil }) => ({ id, codigo, numero, usuario, chapaMovil }));
        
        if (typeof onDataChange === 'function') {
            onDataChange(data);
        }
        console.log(`🔄 Cuadrillas básicas obtenidas de Firestore y actualizadas en caché. Cantidad: ${data.length}`);
    } catch (error) {
        console.error("Error al obtener los datos de cuadrillas de Firestore (Basic):", error);
        if (!hasServedCache) {
            throw error;
        }
    }
}

export const getCuadrillaById = async (id, onDataChange) => {
    const cuadrillasCollection = collection(firestore, 'cuadrillas');
    const q = query(cuadrillasCollection, where('id', '==', id));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
        querySnapshot.forEach((doc) => {
            onDataChange(doc.data())
        });
    } else {
        console.log("No se encontró ningún documento con ese id.");
    }
}

export const getCuadrillaEmailById = async (id) => {
    if (id === null || id === undefined || id === '') return '';
    try {
        const cuadrillasCollection = collection(firestore, 'cuadrillas');
        
        // Buscar por id numérico
        const idNum = Number(id);
        if (!isNaN(idNum)) {
            const q = query(cuadrillasCollection, where('id', '==', idNum));
            const snap = await getDocs(q);
            if (!snap.empty) {
                const email = snap.docs[0].data().correoElectronico;
                if (email && email.trim() !== '') return email.trim();
            }
        }
        
        // Buscar por id de tipo string
        const q2 = query(cuadrillasCollection, where('id', '==', String(id)));
        const snap2 = await getDocs(q2);
        if (!snap2.empty) {
            const email = snap2.docs[0].data().correoElectronico;
            if (email && email.trim() !== '') return email.trim();
        }
    } catch (e) {
        console.error("Error fetching cuadrilla email by id:", e);
    }
    return '';
}

export const sanitizeSeriales = (seriales) => {
    if (!Array.isArray(seriales)) return seriales;
    return seriales.map(s => {
        if (!s || typeof s !== 'object') return s;
        return {
            ...s,
            ...(s.id !== undefined && s.id !== null ? { id: String(s.id).trim() } : {}),
            ...(s.tipo !== undefined && s.tipo !== null ? { tipo: String(s.tipo).trim().toLowerCase() } : {})
        };
    });
};

export const updateCuadrillaFirestore = async (id, data, functionExe, metadatos = {}) => {
    try {
        if (data.seriales && Array.isArray(data.seriales)) {
            data.seriales = sanitizeSeriales(data.seriales);
        }

        // Obtener datos anteriores para comparar si incluye seriales
        let serialesAnteriores = [];
        if (data.seriales && Array.isArray(data.seriales)) {
            const docRef = doc(firestore, "cuadrillas", String(id));
            const snapAnterior = await getDoc(docRef);
            if (snapAnterior.exists()) {
                serialesAnteriores = snapAnterior.data().seriales || [];
            }
        }

        const docRef = doc(firestore, "cuadrillas", String(id));

        // Actualizar solo los campos que especifiques
        await updateDoc(docRef, data);

        console.log("Documento actualizado exitosamente (cuadrillas)");
        
        // Invalida la caché local
        invalidateCuadrillasCache();

        // Registrar cambios como complemento (no afecta la funcionalidad principal)
        try {
            await registrarCambiosCuadrilla(id, data, {
                ...metadatos,
                serialesAnteriores,
                tipoOperacion: metadatos.tipoOperacion || 'ACTUALIZACION_FIRESTORE'
            });
        } catch (errorRegistro) {
            console.error("Error al registrar cambios (complementario):", errorRegistro);
            // No interrumpir el flujo principal
        }
        
        if (functionExe) {
            functionExe();
        }
    } catch (error) {
        console.error("Error al actualizar el documento: ", error);
    }
};

/**
 * Actualiza una cuadrilla en Firestore con registro automático de cambios en seriales
 * @param {string} id - ID de la cuadrilla
 * @param {Object} data - Datos a actualizar
 * @param {Function} functionExe - Función a ejecutar después de la actualización
 * @param {Object} metadatos - Información adicional para el registro
 */
export const updateCuadrillaConRegistro = async (id, data, functionExe, metadatos = {}) => {
    try {
        if (data.seriales && Array.isArray(data.seriales)) {
            data.seriales = sanitizeSeriales(data.seriales);
        }

        const docRef = doc(firestore, "cuadrillas", String(id));
        
        // Obtener datos anteriores para comparar seriales
        const snapAnterior = await getDoc(docRef);
        const datosAnteriores = snapAnterior.exists() ? snapAnterior.data() : {};
        const serialesAnteriores = datosAnteriores.seriales || [];
        
        // Actualizar el documento
        await updateDoc(docRef, data);
        
        // Invalida la caché local
        invalidateCuadrillasCache();

        // Si la actualización incluye seriales, crear registro
        if (data.seriales && Array.isArray(data.seriales)) {
            await registrarActualizacionDirecta({
                tipoOperacion: metadatos.tipoOperacion || 'ACTUALIZACION_CUADRILLA',
                componenteOrigen: metadatos.componenteOrigen || 'CuadrillasService',
                idCuadrilla: id,
                serialesAnteriores,
                serialesNuevos: data.seriales,
                metadatos: {
                    ...metadatos,
                    camposActualizados: Object.keys(data)
                }
            });
        }

        console.log("Documento actualizado exitosamente (cuadrillas) con registro");
        if (functionExe) {
            functionExe();
        }
    } catch (error) {
        console.error("Error al actualizar el documento: ", error);
        throw error;
    }
};

/**
 * Función complementaria para registrar cambios en cuadrillas después de cualquier actualización
 * Se ejecuta DESPUÉS de la actualización principal para no interferir con la funcionalidad existente
 * @param {string} id - ID de la cuadrilla
 * @param {Object} datosActualizados - Datos que se actualizaron
 * @param {Object} metadatos - Información adicional sobre la actualización
 */
export const registrarCambiosCuadrilla = async (id, datosActualizados, metadatos = {}) => {
    try {
        console.log(`📝 Registrando cambios en cuadrilla ${id}`);
        
        // Obtener datos actuales de la cuadrilla después de la actualización
        const docRef = doc(firestore, "cuadrillas", String(id));
        const snap = await getDoc(docRef);
        
        if (!snap.exists()) {
            console.warn(`⚠️ Cuadrilla ${id} no encontrada para registro`);
            return;
        }
        
        const datosActuales = snap.data();
        
        // Si la actualización incluyó seriales, registrar específicamente los cambios en seriales
        if (datosActualizados.seriales && Array.isArray(datosActualizados.seriales)) {
            // Intentar obtener los seriales anteriores desde metadatos o asumir array vacío
            const serialesAnteriores = metadatos.serialesAnteriores || [];
            
            await registrarActualizacionDirecta({
                tipoOperacion: metadatos.tipoOperacion || 'ACTUALIZACION_GENERAL',
                componenteOrigen: metadatos.componenteOrigen || 'CuadrillasService',
                idCuadrilla: id,
                serialesAnteriores,
                serialesNuevos: datosActualizados.seriales,
                metadatos: {
                    ...metadatos,
                    camposActualizados: Object.keys(datosActualizados)
                }
            });
        }
        
        console.log(`✅ Registro de cambios completado para cuadrilla ${id}`);
    } catch (error) {
        console.error(`❌ Error al registrar cambios en cuadrilla ${id}:`, error);
        // No lanzar error para no interrumpir el flujo principal
    }
};

/**
 * Función auxiliar para registrar actualizaciones de cuadrillas después de una transacción
 * Se debe llamar DESPUÉS de que la transacción se haya completado exitosamente
 * @param {string} idCuadrilla - ID de la cuadrilla
 * @param {Array} serialesAnteriores - Seriales antes de la actualización
 * @param {Array} serialesNuevos - Seriales después de la actualización
 * @param {Object} metadatos - Información adicional sobre la actualización
 */
export const registrarActualizacionPostTransaccion = async (idCuadrilla, serialesAnteriores, serialesNuevos, metadatos = {}) => {
    try {
        console.log(`📝 Registrando actualización post-transacción para cuadrilla ${idCuadrilla}`);
        
        // Solo registrar si hay cambios en seriales
        if (serialesAnteriores || serialesNuevos) {
            await registrarActualizacionDirecta({
                tipoOperacion: metadatos.tipoOperacion || 'ACTUALIZACION_TRANSACCION',
                componenteOrigen: metadatos.componenteOrigen || 'CuadrillasService',
                idCuadrilla,
                serialesAnteriores: serialesAnteriores || [],
                serialesNuevos: serialesNuevos || [],
                metadatos: {
                    ...metadatos
                }
            });
        }
        
        console.log(`✅ Registro post-transacción completado para cuadrilla ${idCuadrilla}`);
    } catch (error) {
        console.error(`❌ Error en registro post-transacción para cuadrilla ${idCuadrilla}:`, error);
        // No lanzar error para no interrumpir el flujo principal
    }
};
